import React, { useEffect, useMemo, useState } from 'react'
import { getFieldValue, getInspectorColumns } from '../../config/columns'
import { CONFIG, isSymbolLookupMatch } from '../../config/constants'
import { useColumnVisibility } from '../../hooks/useColumnVisibility'
import { formatISTTime, parseDateAndTimeToMs } from '../../lib/date-utils'
import { TABLE_CLASSES, formatByColumnType } from '../../lib/table-utils'
import { cn } from '../../lib/utils'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { TableVirtuoso } from 'react-virtuoso'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'
import type { InspectionMode, InspectionSource } from './types'
import { formatIndianScale, RenderArrow } from '../../lib/table-utils'
import { VarianceChip } from '../chips/VarianceChip'
import { extractSnapshotOptions } from '../../lib/snapshot-search'

interface InspectorHistoryTableProps {
    symbol: string
    expiry: string
    selectedStrikes: number[]
    rangeStartDate: string
    rangeEndDate: string
    inspectionSource: InspectionSource
    inspectionMode: InspectionMode
    isLive: boolean
    effectiveHistory?: any[] // Allow passing data directly
    timeGap?: number
    setTimeGap?: (gap: number) => void
    className?: string
    // New props for split selection
    ceStrikes?: number[]
    peStrikes?: number[]
}

// ── Arrow Indicator Component (Refactored to use centralized RenderArrow) ──
const ChangeArrow: React.FC<{ current: number | undefined; previous: number | undefined }> = ({
    current,
    previous,
}) => {
    if (current === undefined || previous === undefined || isNaN(current) || isNaN(previous)) return null
    return <RenderArrow value={current - previous} />
}

const InspectorHistoryTableContent: React.FC<InspectorHistoryTableProps> = (props) => {
    const {
        symbol,
        expiry,
        selectedStrikes,
        rangeStartDate,
        rangeEndDate,
        inspectionSource,
        isLive,
        effectiveHistory,
        timeGap = 1,
        setTimeGap,
        className,
        inspectionMode,
        ceStrikes,
        peStrikes,
    } = props

    const history = effectiveHistory || []
    const deferredHistory = React.useDeferredValue(history)

    const sortedHistory = useMemo(() => {
        return [...deferredHistory].sort((a, b) => {
            const timeA = parseDateAndTimeToMs(a.timestampIST || a.timestamp)
            const timeB = parseDateAndTimeToMs(b.timestampIST || b.timestamp)
            return timeB - timeA
        })
    }, [deferredHistory])

    // Column Management
    const defaultColumns = useMemo(
        () => getInspectorColumns().map((c) => ({ id: c.id, label: c.label, visible: true })),
        [],
    )
    const [columns, toggleColumn, isVisible] = useColumnVisibility(
        CONFIG.UI.STORAGE_KEYS.INSPECTOR_COLUMNS,
        defaultColumns,
    )

    const visibleTableColumns = useMemo(() => {
        const visibleIds = new Set(columns.filter((c) => c.visible).map((c) => c.id))
        // Exclude calculated/combined columns from the standard repeated CE/PE loop
        const skipIds = [
            'ce_combined_oi',
            'ce_combined_vol',
            'pe_combined_oi',
            'pe_combined_vol',
            'oi_diff',
            'vol_diff',
            'cum_price',
        ]
        return getInspectorColumns().filter((c) => visibleIds.has(c.id) && !skipIds.includes(c.id))
    }, [columns])

    // ── Helper: Extract a numeric value from an option row ──
    const getNumericField = (opt: any, fieldKeys: readonly string[]): number | undefined => {
        const val = getFieldValue(opt, fieldKeys)
        return typeof val === 'number' && !isNaN(val) ? val : undefined
    }

    // ── Helper: Build enriched row data for one snapshot ──
    const buildRowData = (row: any, allOptions: any[]) => {
        const isMultiple = inspectionMode === 'Multiple' && ceStrikes && peStrikes
        const ceStrike = isMultiple ? ceStrikes[0] : selectedStrikes[0]
        const peStrike = isMultiple ? peStrikes[0] : selectedStrikes[0]

        const baseCeOpt = allOptions.find((o: any) => o.strike === ceStrike) || {}
        const basePeOpt = allOptions.find((o: any) => o.strike === peStrike) || {}

        // Create shallow copies to safely inject nested fields (like marketData.cp)
        const ceOpt = { ...baseCeOpt }
        const peOpt = { ...basePeOpt }

        // Helper to parse strings safely
        const parseOptionalString = (val: any) => {
            if (typeof val === 'number' && !isNaN(val)) return val
            if (typeof val === 'string') return parseFloat(val.replace(/,/g, '')) || 0
            return undefined
        }

        // Get LTP very early to assist with derived calculations
        const ceLtp =
            getNumericField(ceOpt, ['callPrice', 'ceLtp', 'ce_ltp', 'lastPrice']) ??
            getNumericField(ceOpt.ce, ['ltp', 'price']) ??
            0
        const peLtp =
            getNumericField(peOpt, ['putPrice', 'peLtp', 'pe_ltp', 'lastPrice']) ??
            getNumericField(peOpt.pe, ['ltp', 'price']) ??
            0

        const ceChangeAbs =
            getNumericField(ceOpt, ['callChange', 'ceChangeAbs', 'ce_change']) ??
            getNumericField(ceOpt.ce, ['change']) ??
            parseOptionalString(ceOpt.ce?.change) ??
            0
        const peChangeAbs =
            getNumericField(peOpt, ['putChange', 'peChangeAbs', 'pe_change']) ??
            getNumericField(peOpt.pe, ['change']) ??
            parseOptionalString(peOpt.pe?.change) ??
            0

        // Inject deeply nested or string values into the top level for getFieldValue compatibility
        ceOpt.callChangePercent =
            getNumericField(ceOpt, ['callChangePercent', 'ceChangePercent', 'ceChangep']) ??
            getNumericField(ceOpt.marketData, ['cp', 'changePercent']) ??
            parseOptionalString(ceOpt.ce?.changePercent) ??
            parseOptionalString(ceOpt.ce?.marketData?.cp) ??
            parseOptionalString(ceOpt.ce_change_p) ??
            parseOptionalString(ceOpt.change_p)

        // Dynamic Fallback: If publisher exports 0% but we have an absolute change and an LTP, compute it!
        if (!ceOpt.callChangePercent && ceChangeAbs && ceLtp) {
            const ceClose = ceLtp - ceChangeAbs
            if (ceClose !== 0) ceOpt.callChangePercent = (ceChangeAbs / ceClose) * 100
        }
        // Force 0 for visual rendering instead of undefined
        ceOpt.callChangePercent = ceOpt.callChangePercent || 0

        peOpt.putChangePercent =
            getNumericField(peOpt, ['putChangePercent', 'peChangePercent', 'peChangep']) ??
            getNumericField(peOpt.marketData, ['cp', 'changePercent']) ??
            parseOptionalString(peOpt.pe?.changePercent) ??
            parseOptionalString(peOpt.pe?.marketData?.cp) ??
            parseOptionalString(peOpt.pe_change_p) ??
            parseOptionalString(peOpt.change_p)

        if (!peOpt.putChangePercent && peChangeAbs && peLtp) {
            const peClose = peLtp - peChangeAbs
            if (peClose !== 0) peOpt.putChangePercent = (peChangeAbs / peClose) * 100
        }
        peOpt.putChangePercent = peOpt.putChangePercent || 0

        // CE side remaining values
        const ceOI =
            getNumericField(ceOpt, ['callOpenInterest', 'ceOI', 'ce_oi']) ??
            getNumericField(ceOpt.ce, ['oi', 'openInterest'])
        const ceVol =
            getNumericField(ceOpt, ['callVolume', 'ceVol', 'ce_vol']) ?? getNumericField(ceOpt.ce, ['vol', 'volume'])

        // PE side remaining values
        const peOI =
            getNumericField(peOpt, ['putOpenInterest', 'peOI', 'pe_oi']) ??
            getNumericField(peOpt.pe, ['oi', 'openInterest'])
        const peVol =
            getNumericField(peOpt, ['putVolume', 'peVol', 'pe_vol']) ?? getNumericField(peOpt.pe, ['vol', 'volume'])

        // CE Combined = CE strike's CE OI + CE strike's PE OI (same strike, both sides)
        const ceStrikeOptPeOI =
            getNumericField(ceOpt, ['putOpenInterest', 'peOI', 'pe_oi']) ??
            getNumericField(ceOpt.pe, ['oi', 'openInterest'])
        const ceStrikeOptPeVol =
            getNumericField(ceOpt, ['putVolume', 'peVol', 'pe_vol']) ?? getNumericField(ceOpt.pe, ['vol', 'volume'])
        const ceCombinedOI = (ceOI || 0) + (ceStrikeOptPeOI || 0)
        const ceCombinedVol = (ceVol || 0) + (ceStrikeOptPeVol || 0)

        // PE Combined = PE strike's CE OI + PE strike's PE OI (same strike, both sides)
        const peStrikeOptCeOI =
            getNumericField(peOpt, ['callOpenInterest', 'ceOI', 'ce_oi']) ??
            getNumericField(peOpt.ce, ['oi', 'openInterest'])
        const peStrikeOptCeVol =
            getNumericField(peOpt, ['callVolume', 'ceVol', 'ce_vol']) ?? getNumericField(peOpt.ce, ['vol', 'volume'])
        const peCombinedOI = (peStrikeOptCeOI || 0) + (peOI || 0)
        const peCombinedVol = (peStrikeOptCeVol || 0) + (peVol || 0)

        // Differences (PE - CE across strikes)
        const oiDiff = (peOI || 0) - (ceOI || 0)
        const volDiff = (peVol || 0) - (ceVol || 0)

        // Snapshot-level metrics (OI% Variance, Counts)
        const variance = row?.analysis?.variance?.finalVariance ?? row?.oiVariance ?? row?.variance ?? 0
        const totalCount = row?.analysis?.totalCount ?? row?.totalCount ?? 0
        const ceCount = row?.analysis?.ceCount ?? row?.ce_count ?? row?.ceCount ?? 0
        const peCount = row?.analysis?.peCount ?? row?.pe_count ?? row?.peCount ?? 0

        return {
            ceStrike: ceStrike || 0,
            peStrike: peStrike || 0,
            ceOpt,
            peOpt,
            ceLtp,
            ceOI,
            ceVol,
            peLtp,
            peOI,
            peVol,
            ceCombinedOI,
            ceCombinedVol,
            peCombinedOI,
            peCombinedVol,
            oiDiff,
            volDiff,
            variance,
            totalCount,
            ceCount,
            peCount,
        }
    }

    // ── Precompute all enriched row data for arrow comparison ──
    const enrichedRows = useMemo(() => {
        return sortedHistory.map((row, idx) => {
            const globalIdx = idx
            const allOptions = extractSnapshotOptions(row, symbol, expiry)
            const enriched = buildRowData(row, allOptions)
            
            // To calculate change arrows, we only need the exact previous record in time
            const nextIdx = globalIdx + 1
            const prevRow = nextIdx < sortedHistory.length ? sortedHistory[nextIdx] : undefined
            let prevEnriched = undefined
            if (prevRow) {
                const prevOptions = extractSnapshotOptions(prevRow, symbol, expiry)
                prevEnriched = { row: prevRow, ...buildRowData(prevRow, prevOptions) }
            }

            return { row, ...enriched, prevEnriched }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedHistory, selectedStrikes, ceStrikes, peStrikes, inspectionMode, symbol, expiry])

    const getScaledValue = (val: number | undefined, formatType?: string) => {
        if (val === undefined || val === null || isNaN(val)) return undefined
        // Return raw value - formatting is handled by formatByColumnType in display
        return val
    }

    // ── Render Helper: Value with Arrow ──
    const ValueWithArrow: React.FC<{
        current: number | undefined
        previous: number | undefined
        formatType?: string
        isCurrency?: boolean
        suffix?: string
        colorClass?: string
    }> = ({ current, previous, formatType, isCurrency, suffix, colorClass }) => {
        // Formatting logic
        let formatted = '-'
        if (current !== undefined) {
            if (formatType === 'percent') {
                formatted = current.toFixed(2)
            } else {
                formatted = formatIndianScale(current)
            }
        }

        const prevScaled = previous
        const curScaled = current

        return (
            <span className={`inline-flex items-center gap-0.5 ${colorClass || 'text-slate-600 dark:text-slate-400'}`}>
                <span className="tabular-nums text-[11px] font-bold">
                    {formatted}
                    {current !== undefined && suffix ? suffix : ''}
                </span>
                <ChangeArrow current={curScaled} previous={prevScaled} />
            </span>
        )
    }

    return (
        <TableWrapper
            className={cn(
                'w-full shadow-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg transition-colors duration-300 content-auto',
                className,
            )}
        >
            {/* Table Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                    Inspection History
                </h3>
                <div className="flex items-center gap-4">
                    <ColumnSelector columns={columns} onChange={toggleColumn} />
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                            Interval:
                        </span>
                        <select
                            value={timeGap}
                            onChange={(e) => setTimeGap?.(Number(e.target.value))}
                            className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                            disabled={!setTimeGap}
                        >
                            <option value={1}>1 Min</option>
                            <option value={3}>3 Min</option>
                            <option value={5}>5 Min</option>
                            <option value={15}>15 Min</option>
                            <option value={30}>30 Min</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className={`${TABLE_CLASSES.CONTAINER} max-h-[800px] h-[800px]`}>
                <TableVirtuoso
                    data={enrichedRows}
                    style={{ height: '100%' }}
                    fixedHeaderContent={() => (
                        <tr>
                            {/* Time */}
                            <th className="sticky left-0 z-50 px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider backdrop-blur-md">
                                Time
                            </th>

                            {/* CE Columns */}
                            {visibleTableColumns
                                .filter((c) => c.id !== 'cum_price')
                                .map((col) => (
                                    <GhostTh key={`ce-${col.id}`} align="right" color="red" className="top-0! py-2">
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                CE Call
                                            </span>
                                            <span className="leading-tight">{col.label}</span>
                                        </div>
                                    </GhostTh>
                                ))}

                            {/* CE Combined OI */}
                            {isVisible('ce_combined_oi') && (
                                <GhostTh align="right" color="red" className="top-0! py-2">
                                    <div className="flex flex-col items-end gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            CE
                                        </span>
                                        <span className="leading-tight text-[10px]">Comb OI</span>
                                    </div>
                                </GhostTh>
                            )}

                            {/* CE Combined Volume */}
                            {isVisible('ce_combined_vol') && (
                                <GhostTh align="right" color="red" className="top-0! py-2">
                                    <div className="flex flex-col items-end gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            CE
                                        </span>
                                        <span className="leading-tight text-[10px]">Comb Vol</span>
                                    </div>
                                </GhostTh>
                            )}

                            {/* CE Strike */}
                            <GhostTh align="center" color="red" className="top-0! min-w-[80px] py-2">
                                <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                        CE
                                    </span>
                                    <span className="leading-tight">Strike</span>
                                </div>
                            </GhostTh>

                            {/* Combined: OI Diff + Vol Diff + Total Premium */}
                            {isVisible('oi_diff') && (
                                <GhostTh align="center" color="purple" className="top-0! py-2">
                                    <div className="flex flex-col items-center gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            Diff
                                        </span>
                                        <span className="leading-tight text-[10px]">OI Diff</span>
                                    </div>
                                </GhostTh>
                            )}
                            {isVisible('vol_diff') && (
                                <GhostTh align="center" color="purple" className="top-0! py-2">
                                    <div className="flex flex-col items-center gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            Diff
                                        </span>
                                        <span className="leading-tight text-[10px]">Vol Diff</span>
                                    </div>
                                </GhostTh>
                            )}

                            {visibleTableColumns.find((c) => c.id === 'cum_price') && (
                                <GhostTh align="center" color="purple" className="top-0! py-2">
                                    <div className="flex flex-col items-center gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            Combined
                                        </span>
                                        <span className="leading-tight">Total Prem</span>
                                    </div>
                                </GhostTh>
                            )}

                            {/* PE Strike */}
                            <GhostTh align="center" color="green" className="top-0! min-w-[80px] py-2">
                                <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                        PE
                                    </span>
                                    <span className="leading-tight">Strike</span>
                                </div>
                            </GhostTh>

                            {/* PE Columns */}
                            {visibleTableColumns
                                .filter((c) => c.id !== 'cum_price')
                                .map((col) => (
                                    <GhostTh key={`pe-${col.id}`} align="left" color="green" className="top-0! py-2">
                                        <div className="flex flex-col items-start gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                PE Put
                                            </span>
                                            <span className="leading-tight">{col.label}</span>
                                        </div>
                                    </GhostTh>
                                ))}

                            {/* PE Combined OI */}
                            {isVisible('pe_combined_oi') && (
                                <GhostTh align="left" color="green" className="top-0! py-2">
                                    <div className="flex flex-col items-start gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            PE
                                        </span>
                                        <span className="leading-tight text-[10px]">Comb OI</span>
                                    </div>
                                </GhostTh>
                            )}

                            {/* PE Combined Volume */}
                            {isVisible('pe_combined_vol') && (
                                <GhostTh align="left" color="green" className="top-0! py-2">
                                    <div className="flex flex-col items-start gap-0.5">
                                        <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                            PE
                                        </span>
                                        <span className="leading-tight text-[10px]">Comb Vol</span>
                                    </div>
                                </GhostTh>
                            )}
                        </tr>
                    )}
                    components={{
                        Table: (props) => <table {...props} className={TABLE_CLASSES.TABLE} />,
                        TableHead: React.forwardRef((props, ref) => <thead {...props} ref={ref as any} className={TABLE_CLASSES.THEAD} />),
                        TableBody: React.forwardRef((props, ref) => <tbody {...props} ref={ref as any} className="divide-y divide-slate-100 dark:divide-slate-800 bg-white/40 dark:bg-slate-900/40" />),
                        TableRow: (props) => <tr {...props} className={TABLE_CLASSES.TR} />
                    }}
                    itemContent={(idx, enriched) => {
                        const { row, ceOpt, peOpt, ceStrike, peStrike, prevEnriched } = enriched

                        return (
                            <>
                                {/* Time (Sticky) */}
                                <td className="sticky left-0 z-10 px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm group-hover:bg-slate-50 dark:group-hover:bg-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] transition-colors">
                                    {formatISTTime(row.timestampIST || row.timestamp)}
                                </td>

                                {/* CE Data Cells with Arrows */}
                                {visibleTableColumns
                                    .filter((c) => c.id !== 'cum_price')
                                    .map((col) => {
                                        const ceFields = Array.isArray(col.ceField)
                                            ? col.ceField
                                            : col.ceField
                                              ? [col.ceField]
                                              : []
                                        let val = getFieldValue(ceOpt, ceFields as readonly string[])
                                        if (typeof val === 'string') val = parseFloat(val.replace(/,/g, ''))

                                        let prevVal = prevEnriched
                                            ? getFieldValue(prevEnriched.ceOpt, ceFields as readonly string[])
                                            : undefined
                                        if (typeof prevVal === 'string')
                                            prevVal = parseFloat(prevVal.replace(/,/g, ''))

                                        const isChange = col.id === 'change_p' || col.id === 'oi_change'
                                        const colorClass =
                                            isChange && typeof val === 'number' && !isNaN(val)
                                                ? val > 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : val < 0
                                                      ? 'text-rose-600 dark:text-rose-400'
                                                      : 'text-slate-600 dark:text-slate-400'
                                                : 'text-slate-600 dark:text-slate-400'

                                        return (
                                            <td
                                                key={`ce-${col.id}`}
                                                className={`${TABLE_CLASSES.TD} text-right text-xs`}
                                            >
                                                <ValueWithArrow
                                                    current={
                                                        typeof val === 'number' && !isNaN(val) ? val : undefined
                                                    }
                                                    previous={
                                                        typeof prevVal === 'number' && !isNaN(prevVal)
                                                            ? prevVal
                                                            : undefined
                                                    }
                                                    formatType={col.format}
                                                    isCurrency={col.format === 'decimal'}
                                                    suffix={col.format === 'percent' ? '%' : undefined}
                                                    colorClass={colorClass}
                                                />
                                            </td>
                                        )
                                    })}

                                {/* CE Combined OI */}
                                {isVisible('ce_combined_oi') && (
                                    <td className={`${TABLE_CLASSES.TD} text-right text-xs`}>
                                        <ValueWithArrow
                                            current={enriched.ceCombinedOI}
                                            previous={prevEnriched?.ceCombinedOI}
                                            formatType="lakh"
                                            colorClass="text-slate-600 dark:text-slate-400"
                                        />
                                    </td>
                                )}

                                {/* CE Combined Volume */}
                                {isVisible('ce_combined_vol') && (
                                    <td className={`${TABLE_CLASSES.TD} text-right text-xs`}>
                                        <ValueWithArrow
                                            current={enriched.ceCombinedVol}
                                            previous={prevEnriched?.ceCombinedVol}
                                            formatType="crore"
                                            colorClass="text-slate-600 dark:text-slate-400"
                                        />
                                    </td>
                                )}

                                {/* CE Strike */}
                                <td className={`${TABLE_CLASSES.TD} text-center`}>
                                    <StrikeChip
                                        strike={ceStrike}
                                        label={undefined}
                                        type="neutral"
                                        className="bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-800"
                                    />
                                </td>

                                {/* OI Difference */}
                                {isVisible('oi_diff') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <div className="flex justify-center">
                                            <VarianceChip
                                                value={enriched.oiDiff}
                                                previousValue={prevEnriched?.oiDiff}
                                                formatType="lakh"
                                            />
                                        </div>
                                    </td>
                                )}

                                {/* Volume Difference */}
                                {isVisible('vol_diff') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <div className="flex justify-center">
                                            <VarianceChip
                                                value={enriched.volDiff}
                                                previousValue={prevEnriched?.volDiff}
                                                formatType="crore"
                                            />
                                        </div>
                                    </td>
                                )}

                                {/* Combined Premium */}
                                {visibleTableColumns.find((c) => c.id === 'cum_price') && (
                                    <td
                                        className={`${TABLE_CLASSES.TD} bg-indigo-50/20 dark:bg-indigo-900/20 text-center font-black text-indigo-700 dark:text-indigo-400 shadow-inner`}
                                    >
                                        {(() => {
                                            const ceP = enriched.ceLtp || 0
                                            const peP = enriched.peLtp || 0
                                            const total = ceP + peP
                                            const prevTotal =
                                                (prevEnriched?.ceLtp || 0) + (prevEnriched?.peLtp || 0)
                                            return (
                                                <span className="inline-flex items-center gap-0 text-indigo-700 dark:text-indigo-400">
                                                    <NumberCell
                                                        value={total}
                                                        className="text-indigo-700 dark:text-indigo-400"
                                                    />
                                                    <ChangeArrow
                                                        current={total}
                                                        previous={prevTotal || undefined}
                                                    />
                                                </span>
                                            )
                                        })()}
                                    </td>
                                )}

                                {/* PE Strike */}
                                <td className={`${TABLE_CLASSES.TD} text-center`}>
                                    <StrikeChip
                                        strike={peStrike}
                                        label={undefined}
                                        type="neutral"
                                        className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800"
                                    />
                                </td>

                                {/* PE Data Cells with Arrows */}
                                {visibleTableColumns
                                    .filter((c) => c.id !== 'cum_price')
                                    .map((col) => {
                                        const peFields = Array.isArray(col.peField)
                                            ? col.peField
                                            : col.peField
                                              ? [col.peField]
                                              : []
                                        let val = getFieldValue(peOpt, peFields as readonly string[])
                                        if (typeof val === 'string') val = parseFloat(val.replace(/,/g, ''))

                                        let prevVal = prevEnriched
                                            ? getFieldValue(prevEnriched.peOpt, peFields as readonly string[])
                                            : undefined
                                        if (typeof prevVal === 'string')
                                            prevVal = parseFloat(prevVal.replace(/,/g, ''))

                                        const isChange = col.id === 'change_p' || col.id === 'oi_change'
                                        const colorClass =
                                            isChange && typeof val === 'number' && !isNaN(val)
                                                ? val > 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : val < 0
                                                      ? 'text-rose-600 dark:text-rose-400'
                                                      : 'text-slate-600 dark:text-slate-400'
                                                : 'text-slate-600 dark:text-slate-400'

                                        return (
                                            <td
                                                key={`pe-${col.id}`}
                                                className={`${TABLE_CLASSES.TD} text-left text-xs`}
                                            >
                                                <ValueWithArrow
                                                    current={
                                                        typeof val === 'number' && !isNaN(val) ? val : undefined
                                                    }
                                                    previous={
                                                        typeof prevVal === 'number' && !isNaN(prevVal)
                                                            ? prevVal
                                                            : undefined
                                                    }
                                                    formatType={col.format}
                                                    isCurrency={col.format === 'decimal'}
                                                    suffix={col.format === 'percent' ? '%' : undefined}
                                                    colorClass={colorClass}
                                                />
                                            </td>
                                        )
                                    })}

                                {/* PE Combined OI */}
                                {isVisible('pe_combined_oi') && (
                                    <td className={`${TABLE_CLASSES.TD} text-left text-xs`}>
                                        <ValueWithArrow
                                            current={enriched.peCombinedOI}
                                            previous={prevEnriched?.peCombinedOI}
                                            formatType="lakh"
                                            colorClass="text-slate-600 dark:text-slate-400"
                                        />
                                    </td>
                                )}

                                {isVisible('pe_combined_vol') && (
                                    <td className={`${TABLE_CLASSES.TD} text-left text-xs`}>
                                        <ValueWithArrow
                                            current={enriched.peCombinedVol}
                                            previous={prevEnriched?.peCombinedVol}
                                            formatType="crore"
                                            colorClass="text-slate-600 dark:text-slate-400"
                                        />
                                    </td>
                                )}
                            </>
                        )
                    }}
                />
            </div>
        </TableWrapper>
    )
}

export const InspectorHistoryTable = (props: InspectorHistoryTableProps) => (
    <WidgetErrorBoundary fallbackDescription="Inspector History">
        <InspectorHistoryTableContent {...props} />
    </WidgetErrorBoundary>
)
